/**
 * Crear una comunidad.
 *
 * Lo que esta pantalla tiene que dejar clarísimo, antes y después de enviar,
 * es que la comunidad NO aparece de una: queda esperando aprobación. Si eso
 * se dice solo al final, la persona llena todo esperando otra cosa.
 */
import { useState } from 'react';
import type { FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { COMMUNITY_LIMITS, type CommunityCategory } from '@tennisup/contracts';

import { Alert } from '../components/Alert';
import { AppLayout } from '../components/AppLayout';
import { BackButton } from '../components/BackButton';
import { CategoryPicker } from '../components/CategoryPicker';
import { Field } from '../components/Field';
import { ImagePicker } from '../components/ImagePicker';
import { api } from '../lib/api';
import { errorSummary } from '../lib/errorMessage';

export function NewCommunityPage(): JSX.Element {
  const { t } = useTranslation();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [locationText, setLocationText] = useState('');
  const [whatsappGroupUrl, setWhatsappGroupUrl] = useState('');
  const [logo, setLogo] = useState<File | null>(null);
  const [categories, setCategories] = useState<CommunityCategory[]>([]);

  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [done, setDone] = useState(false);

  const canSubmit =
    name.trim().length >= COMMUNITY_LIMITS.nameMin &&
    description.trim().length >= COMMUNITY_LIMITS.descriptionMin &&
    locationText.trim().length > 0 &&
    // Al menos una categoría: sin ninguna, el club existe y nadie se puede
    // inscribir en nada.
    categories.length > 0 &&
    !sending;

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (!canSubmit) return;

    setSending(true);
    setError(null);
    setFieldErrors({});
    try {
      await api.createCommunity(
        { name, description, locationText, whatsappGroupUrl },
        categories,
        logo,
      );
      setDone(true);
    } catch (caught) {
      // `errorSummary` y no `errorMessage`: los mensajes por campo se pintan
      // abajo, al lado de su input, y repetirlos acá arriba los dice dos
      // veces. Ver errorMessage.ts.
      setError(errorSummary(t, caught));
      // Los errores por campo vienen del servidor y son los únicos que esta
      // pantalla puede mostrar campo por campo: acá no se repite la
      // validación del dominio (qué es un enlace de WhatsApp válido, por
      // ejemplo) porque tener dos versiones de esa regla significa que un día
      // van a discrepar.
      const detalle = (caught as { fieldErrors?: Record<string, string> }).fieldErrors;
      if (detalle !== undefined) setFieldErrors(detalle);
    } finally {
      setSending(false);
    }
  }

  if (done) {
    return (
      <AppLayout>
        <h1>{t('newCommunity.doneTitle')}</h1>
        <Alert kind="success">{t('newCommunity.doneBody')}</Alert>
        <div className="links">
          <Link to="/clubes">{t('newCommunity.backToList')}</Link>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      {/* A la lista de clubes, que es de donde se llega a crear uno. La
          pantalla de «listo» de arriba no lo lleva: ya tiene su enlace, y
          volver desde ahí sería volver al formulario que se acaba de
          mandar. */}
      <BackButton to="/clubes" />
      <h1>{t('newCommunity.title')}</h1>
      <p className="lead">{t('newCommunity.lead')}</p>
      <Alert kind="info">{t('newCommunity.needsApproval')}</Alert>

      {error !== null && <Alert kind="error">{error}</Alert>}

      <form onSubmit={(event) => void onSubmit(event)} noValidate>
        <Field
          id="community-name"
          label={t('newCommunity.name')}
          hint={t('common.required')}
          error={fieldErrors['name']}
        >
          {(control) => (
            <input
              {...control}
              value={name}
              maxLength={COMMUNITY_LIMITS.nameMax}
              onChange={(e) => setName(e.target.value)}
            />
          )}
        </Field>

        <Field
          id="community-location"
          label={t('newCommunity.location')}
          help={t('newCommunity.locationHelp')}
          hint={t('common.required')}
          error={fieldErrors['locationText']}
        >
          {(control) => (
            <input
              {...control}
              value={locationText}
              maxLength={COMMUNITY_LIMITS.locationMax}
              onChange={(e) => setLocationText(e.target.value)}
            />
          )}
        </Field>

        <Field
          id="community-description"
          label={t('newCommunity.description')}
          help={t('newCommunity.descriptionHelp')}
          hint={t('common.required')}
          error={fieldErrors['description']}
        >
          {(control) => (
            <textarea
              {...control}
              rows={4}
              value={description}
              maxLength={COMMUNITY_LIMITS.descriptionMax}
              onChange={(e) => setDescription(e.target.value)}
            />
          )}
        </Field>

        <Field
          id="community-whatsapp"
          label={t('newCommunity.whatsapp')}
          help={t('newCommunity.whatsappHelp')}
          hint={t('common.optional')}
          error={fieldErrors['whatsappGroupUrl']}
        >
          {(control) => (
            <input
              {...control}
              type="url"
              inputMode="url"
              placeholder="https://chat.whatsapp.com/…"
              value={whatsappGroupUrl}
              maxLength={COMMUNITY_LIMITS.whatsappUrlMax}
              onChange={(e) => setWhatsappGroupUrl(e.target.value)}
            />
          )}
        </Field>

        <ImagePicker
          id="community-logo"
          label={t('newCommunity.logo')}
          help={t('newCommunity.logoHelp')}
          value={logo}
          onChange={setLogo}
          disabled={sending}
        />

        <fieldset>
          <legend>{t('categories.title')}</legend>
          <p className="help">{t('categories.help')}</p>
          {fieldErrors['categories'] !== undefined && (
            <Alert kind="error">{fieldErrors['categories']}</Alert>
          )}
          <CategoryPicker value={categories} onChange={setCategories} disabled={sending} />
        </fieldset>

        <button type="submit" disabled={!canSubmit}>
          {sending ? t('common.sending') : t('newCommunity.submit')}
        </button>
      </form>
    </AppLayout>
  );
}
